import { useEffect, useRef } from 'react';

const WHATSAPP_URL = 'https://wa.me/5579999778899?text=Olá,%20gostaria%20de%20agendar%20uma%20consulta%20jurídica.';

export default function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    const t = setTimeout(() => {
      el.style.transition = 'opacity 1.2s ease, transform 1.2s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, 150);
    return () => clearTimeout(t);
  }, []);

  // Subtle Ken Burns zoom on background image
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    img.style.transform = 'scale(1)';
    img.style.transition = 'transform 12s ease-out';
    const t = setTimeout(() => {
      img.style.transform = 'scale(1.07)';
    }, 200);
    return () => clearTimeout(t);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const top = (el as HTMLElement).getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* ── Background Layer ── */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Main photo */}
        <img
          ref={imgRef}
          src="https://readdy.ai/api/search-image?query=cinematic%20premium%20law%20office%20interior%20with%20tall%20dark%20wooden%20bookshelves%20filled%20with%20law%20books%2C%20professional%20lawyer%20silhouette%20at%20desk%2C%20dramatic%20warm%20golden%20desk%20lamp%20illuminating%20dark%20room%2C%20rich%20mahogany%20furniture%2C%20elegant%20leather%20chair%2C%20scales%20of%20justice%20ornament%2C%20deep%20shadows%20with%20warm%20amber%20accent%20light%2C%20editorial%20photography%2C%20wide%20angle%2C%20ultra-detailed%2C%20moody%20and%20authoritative%20atmosphere&width=1920&height=1080&seq=herolaw2025&orientation=landscape"
          alt="Escritório de advocacia premium"
          className="w-full h-full object-cover object-center"
          style={{ willChange: 'transform' }}
        />

        {/* Multi-layer overlays for depth and readability */}
        {/* Layer 1 – base dark fill */}
        <div className="absolute inset-0 bg-[#060f16]/55" />
        {/* Layer 2 – directional gradient: strong left, lighter right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(105deg, rgba(6,15,22,0.75) 0%, rgba(23,58,77,0.65) 45%, rgba(31,75,99,0.25) 100%)',
          }}
        />
        {/* Layer 3 – vertical vignette top & bottom */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(6,15,22,0.55) 0%, transparent 30%, transparent 65%, rgba(6,15,22,0.70) 100%)',
          }}
        />
        {/* Layer 4 – subtle gold tint at far left edge */}
        <div
          className="absolute inset-y-0 left-0 w-2/5"
          style={{
            background:
              'linear-gradient(to right, rgba(198,147,90,0.06) 0%, transparent 100%)',
          }}
        />
      </div>

      {/* ── Decorative lines (subtle, right side) ── */}
      <div className="absolute right-0 top-0 bottom-0 w-2/5 pointer-events-none hidden xl:block overflow-hidden">
        <div
          className="absolute top-1/4 right-24 w-64 h-64 rounded-full border border-[#C6935A]/15"
        />
        <div
          className="absolute top-[35%] right-32 w-40 h-40 rounded-full border border-[#D4A873]/10"
        />
        <div
          className="absolute bottom-1/4 right-28 w-24 h-24 border border-[#C6935A]/10"
          style={{ transform: 'rotate(45deg)' }}
        />
        {/* vertical gold accent line */}
        <div className="absolute top-24 bottom-24 right-16 w-px bg-gradient-to-b from-transparent via-[#C6935A]/20 to-transparent" />
      </div>

      {/* ── Main Content ── */}
      <div
        ref={ref}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 pt-36 pb-24"
      >
        <div className="max-w-3xl">
          {/* Eyebrow label */}
          <div className="flex items-center gap-3 mb-7">
            <div className="w-8 h-px bg-[#C6935A]" />
            <p className="text-[#D4A873] text-xs font-600 tracking-[3px] uppercase">
              Advocacia Consultiva · Aracaju, SE
            </p>
          </div>

          {/* H1 */}
          <h1 className="text-5xl lg:text-7xl font-800 text-white leading-tight mb-7">
            Consultoria estratégica com{' '}
            <span
              className="text-[#C6935A]"
              style={{ textShadow: '0 0 40px rgba(198,147,90,0.35)' }}
            >
              excelência
            </span>{' '}
            e soluções.
          </h1>

          {/* Subtitle */}
          <p className="text-lg lg:text-xl text-white/75 font-300 leading-relaxed mb-12 max-w-2xl">
            Atuação profissional focada em análise técnica, segurança jurídica e tomada de decisões assertivas.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 bg-[#C6935A] hover:bg-[#A8763F] text-white font-600 px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 text-base whitespace-nowrap"
              style={{ boxShadow: '0 8px 32px rgba(198,147,90,0.35)' }}
            >
              <i className="ri-whatsapp-line text-xl" />
              Falar com o advogado
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-10 mt-20 pt-10 border-t border-white/10">
            {[
              { number: '100%', label: 'Atendimento personalizado' },
              { number: 'OAB/SE', label: 'Regularmente inscrito' },
              { number: '24h', label: 'Resposta pelo WhatsApp' },
            ].map((stat) => (
              <div key={stat.label}>
                <p
                  className="text-[#D4A873] text-3xl font-700"
                  style={{ textShadow: '0 0 20px rgba(212,168,115,0.3)' }}
                >
                  {stat.number}
                </p>
                <p className="text-white/55 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Scroll Indicator ── */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-px h-12 bg-gradient-to-b from-transparent to-[#C6935A]" />
        <i className="ri-arrow-down-s-line text-[#C6935A] text-xl w-6 h-6 flex items-center justify-center" />
      </div>
    </section>
  );
}
